import { useState, useCallback, useEffect } from "react";
import {
  Users,
  Globe,
  Database,
  FileSpreadsheet,
  RefreshCw,
  Search,
  Zap,
  Trash2,
  X,
  AlertTriangle,
  Loader2,
  Sparkles,
  Mail,
  Send,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { DataTablePagination } from "../../components/ui/data-table";
import { UnderlineTabs } from "../../components/ui/underline-tabs";
import { LeadsTable } from "../../components/admin/LeadsTable";
import { MultiSelectDropdown } from "../../components/admin/MultiSelectDropdown";
import { StoreCensusFilterBuilder } from "../../components/admin/StoreCensusFilterBuilder";
import { SavedFiltersSidebar } from "../../components/admin/SavedFiltersSidebar";
import { VERTICAL_OPTIONS } from "../../constants/verticals";
import {
  adminLeadsService,
  type Lead,
  type LeadStats,
  type SavedFilter,
  type GenerateLeadsResponse,
} from "../../services/admin/leads.admin.service";
import { useAdminSocket, type EmailValidationUpdate } from "../../hooks/useAdminSocket";

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  warm?: boolean;
}

function StatCard({ title, value, icon, warm }: StatCardProps) {
  return (
    <div className="bg-transparent border border-border rounded-xl transition-all duration-300 hover:border-[#d7785b]/40">
      <div className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
          </div>
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center border ${
              warm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            }`}
          >
            <div className={warm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="bg-transparent border border-border rounded-xl animate-pulse p-6">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
        <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
      </div>
    </div>
  );
}

type TabKey = "all" | "generate";

const GENERATE_BATCH_SIZES = Array.from({ length: 20 }, (_, i) => (i + 1) * 50);

export default function AdminLeads() {
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  // Stats
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // All Leads tab state
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [contactTypeFilter, setContactTypeFilter] = useState("");
  const [savedFilterIdFilter, setSavedFilterIdFilter] = useState("");
  const [verticalFilter, setVerticalFilter] = useState<string[]>([]);
  const [limit, setLimit] = useState(25);

  // Generate tab state
  const [scFilters, setScFilters] = useState<Record<string, any>>({});
  const [generatedLeads, setGeneratedLeads] = useState<Lead[]>([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateResult, setGenerateResult] =
    useState<GenerateLeadsResponse | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [generatePageSize, setGeneratePageSize] = useState(100);
  const generatePreviewRows = Math.min(generatePageSize, 200);

  // Saved filters
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(true);

  // Selection & enrichment
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [enriching, setEnriching] = useState(false);

  // Email validation progress (started automatically after enrich)
  const [validationProgress, setValidationProgress] = useState<{
    jobId: string;
    phase: string;
    processed: number;
    total: number;
    percentComplete: number;
  } | null>(null);

  // Purge modal
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDates, setPurgeDates] = useState<{ date: string; count: number }[]>([]);
  const [purgeDatesLoading, setPurgeDatesLoading] = useState(false);
  const [purging, setPurging] = useState<string | null>(null);
  const [confirmDate, setConfirmDate] = useState<{ date: string; count: number } | null>(null);

  // Send email
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailErrorModal, setEmailErrorModal] = useState<{
    open: boolean;
    leadsWithoutEmails: Lead[];
  }>({ open: false, leadsWithoutEmails: [] });
  const [emailConfirmModal, setEmailConfirmModal] = useState<{
    open: boolean;
    eligibleLeads: Lead[];
  }>({ open: false, eligibleLeads: [] });

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await adminLeadsService.getLeadStats();
      setStats(data);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const data = await adminLeadsService.getLeads({
        page,
        limit,
        search: searchQuery || undefined,
        source: sourceFilter || undefined,
        contactType: (contactTypeFilter as any) || undefined,
        savedFilterId: savedFilterIdFilter || undefined,
        vertical: verticalFilter.length ? verticalFilter : undefined,
      });
      setLeads(data.data);
      setTotalPages(data.pagination.totalPages);
      setTotalLeads(data.pagination.total);
    } catch {
      toast.error("Failed to load leads");
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  }, [page, limit, searchQuery, sourceFilter, contactTypeFilter, savedFilterIdFilter, verticalFilter]);

  // Socket-driven validation updates (placed after fetchLeads to avoid dependency issues)
  const handleValidationUpdate = useCallback(
    (update: EmailValidationUpdate) => {
      if (update.phase === "started") {
        toast(`Validation started — ${update.totalEmails} emails queued`, {
          icon: "🔍",
        });
        setValidationProgress({
          jobId: update.jobId,
          phase: "started",
          processed: 0,
          total: update.totalEmails ?? 0,
          percentComplete: 0,
        });
      } else if (update.phase === "progress" || update.phase === "running" || update.phase === "parsing" || update.phase === "parsed") {
        setValidationProgress((prev) => ({
          jobId: update.jobId,
          phase: "progress",
          processed: update.processed ?? prev?.processed ?? 0,
          total: update.total ?? prev?.total ?? 0,
          percentComplete: update.percentComplete ?? prev?.percentComplete ?? 0,
        }));
      } else if (update.phase === "completed") {
        const s = update.summary;
        const parts: string[] = [];
        if (s) {
          if (s.valid) parts.push(`${s.valid} valid`);
          if (s.catchall) parts.push(`${s.catchall} catchall`);
          if (s.invalid) parts.push(`${s.invalid} invalid`);
          if (s.unknown) parts.push(`${s.unknown} unknown`);
          if (s.disposable) parts.push(`${s.disposable} disposable`);
        }
        toast.success(
          `Email validation complete — ${parts.join(", ") || "done"}`
        );
        setValidationProgress(null);
        fetchLeads();
      } else if (update.phase === "failed") {
        toast.error("Email validation failed. You can retry.");
        setValidationProgress(null);
        setLeads((prev) =>
          prev.map((l) =>
            l.emailValidationStatus === "pending" ||
            l.emailValidationStatus === "in_progress"
              ? { ...l, emailValidationStatus: "failed" as const }
              : l
          )
        );
      }
    },
    [fetchLeads]
  );

  const { pollJobStatus } = useAdminSocket({ onValidationUpdate: handleValidationUpdate });

  // HTTP fallback: when the banner is visible, poll the backend every 15 s so
  // we sync even if the WebSocket event was missed (hot-reload, brief disconnect, etc.)
  useEffect(() => {
    const jobId = validationProgress?.jobId;
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const result = await pollJobStatus(jobId);
        if (result?.jobStatus === "complete") {
          handleValidationUpdate({
            type: "email_validation_update",
            phase: "completed",
            jobId,
            summary: result.summary,
          });
        } else if (result?.jobStatus === "failed") {
          handleValidationUpdate({
            type: "email_validation_update",
            phase: "failed",
            jobId,
          });
        } else if (result?.processed !== undefined) {
          handleValidationUpdate({
            type: "email_validation_update",
            phase: "progress",
            jobId,
            processed: result.processed,
            total: result.total,
          });
        }
      } catch {
        // silent — backend fallback poll is best-effort
      }
    }, 15_000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationProgress?.jobId]);

  // Fetch saved filters
  const fetchSavedFilters = useCallback(async () => {
    setSavedFiltersLoading(true);
    try {
      const data = await adminLeadsService.getSavedFilters();
      setSavedFilters(data);
    } catch {
      // silent
    } finally {
      setSavedFiltersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSavedFilters();
  }, [fetchStats, fetchSavedFilters]);

  useEffect(() => {
    if (activeTab === "all") {
      fetchLeads();
    }
  }, [activeTab, fetchLeads]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === "all") {
        setPage(1);
        fetchLeads();
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, sourceFilter, contactTypeFilter, savedFilterIdFilter, verticalFilter]);

  // Generate leads from StoreCensus
  const handleGenerate = async () => {
    const hasFilters = Object.values(scFilters).some((v) => {
      if (v === undefined || v === null || v === "" || v === false) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    });
    if (!hasFilters) {
      toast.error("Please add at least one filter");
      return;
    }

    setGenerateLoading(true);
    setGenerateResult(null);
    try {
      const result = await adminLeadsService.generateLeads(scFilters, {
        pageSize: generatePageSize,
      });
      setGenerateResult(result);
      toast.success(
        `Imported ${result.imported} of ${result.fetched} fetched (${result.skipped} skipped); ${Number(result.total).toLocaleString()} total matching Store Census`
      );
      // Refresh the leads list and stats
      fetchLeads();
      fetchStats();
      // Fetch generated leads for display
      const leadsData = await adminLeadsService.getLeads({
        source: "store_census",
        limit: generatePreviewRows,
        sortBy: "createdAt",
        sortDir: "desc",
      });
      setGeneratedLeads(leadsData.data);
    } catch (error: any) {
      toast.error(error.message || "Failed to generate leads");
    } finally {
      setGenerateLoading(false);
    }
  };

  // Load more from cursor
  const handleLoadMore = async () => {
    if (!generateResult?.nextCursor || !generateResult.hasMore) return;
    setLoadingMore(true);
    try {
      const result = await adminLeadsService.generateLeads(scFilters, {
        pageSize: generatePageSize,
        cursor: generateResult.nextCursor,
      });
      setGenerateResult(result);
      toast.success(
        `Imported ${result.imported} more of ${result.fetched} fetched; ${Number(result.total).toLocaleString()} total matching Store Census`
      );
      const leadsData = await adminLeadsService.getLeads({
        source: "store_census",
        limit: generatePreviewRows,
        sortBy: "createdAt",
        sortDir: "desc",
      });
      setGeneratedLeads(leadsData.data);
      fetchStats();
    } catch (error: any) {
      toast.error(error.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  // Save filter
  const handleSaveFilter = async (name: string, description: string) => {
    try {
      await adminLeadsService.createSavedFilter({
        name,
        description: description || undefined,
        filters: scFilters,
      });
      toast.success("Filter saved");
      fetchSavedFilters();
    } catch (error: any) {
      toast.error(error.message || "Failed to save filter");
    }
  };

  // Load saved filter into builder
  const handleLoadFilter = (filter: SavedFilter) => {
    setScFilters(filter.filters);
    toast.success(`Loaded "${filter.name}"`);
  };

  // Run saved filter
  const handleRunSavedFilter = async (filter: SavedFilter) => {
    setScFilters(filter.filters);
    setGenerateLoading(true);
    setGenerateResult(null);
    try {
      const result = await adminLeadsService.runSavedFilter(filter.id, {
        pageSize: generatePageSize,
      });
      setGenerateResult(result);
      toast.success(
        `Imported ${result.imported} leads from "${filter.name}" (${result.fetched} fetched; ${Number(result.total).toLocaleString()} matching)`
      );
      fetchLeads();
      fetchStats();
      const leadsData = await adminLeadsService.getLeads({
        source: "store_census",
        limit: generatePreviewRows,
        sortBy: "createdAt",
        sortDir: "desc",
      });
      setGeneratedLeads(leadsData.data);
      fetchSavedFilters();
    } catch (error: any) {
      toast.error(error.message || "Failed to run filter");
    } finally {
      setGenerateLoading(false);
    }
  };

  // Delete saved filter
  const handleDeleteFilter = async (id: string) => {
    try {
      await adminLeadsService.deleteSavedFilter(id);
      toast.success("Filter deleted");
      fetchSavedFilters();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete filter");
    }
  };

  // Set default
  const handleSetDefault = async (id: string, isDefault: boolean) => {
    try {
      await adminLeadsService.updateSavedFilter(id, { isDefault });
      fetchSavedFilters();
    } catch (error: any) {
      toast.error(error.message || "Failed to update filter");
    }
  };

  const openPurgeModal = async () => {
    setPurgeOpen(true);
    setPurgeDatesLoading(true);
    setConfirmDate(null);
    try {
      const dates = await adminLeadsService.getPurgeDates();
      setPurgeDates(dates);
    } catch (error: any) {
      toast.error(error.message || "Failed to load purge dates");
    } finally {
      setPurgeDatesLoading(false);
    }
  };

  const handlePurge = async (date: string) => {
    setPurging(date);
    try {
      const result = await adminLeadsService.purgeByDate(date);
      toast.success(`Purged ${result.deleted} leads from ${formatPurgeDate(date)}`);
      setConfirmDate(null);
      setPurgeDates((prev) => prev.filter((d) => d.date !== date));
      fetchLeads();
      fetchStats();
      if (purgeDates.length <= 1) {
        setPurgeOpen(false);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to purge leads");
    } finally {
      setPurging(null);
    }
  };

  // Selection handlers
  const handleToggleSelect = (id: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedLeadIds.size === leads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(leads.map((l) => l.id)));
    }
  };

  const handleEnrichSelected = async () => {
    if (selectedLeadIds.size === 0) return;
    setEnriching(true);
    try {
      const result = await adminLeadsService.enrichLeads(
        Array.from(selectedLeadIds)
      );
      const parts: string[] = [];
      if (result.enriched > 0) parts.push(`${result.enriched} enriched`);
      if (result.skippedNoContact > 0) parts.push(`${result.skippedNoContact} no contact`);
      if (result.skippedNoLeadId > 0) parts.push(`${result.skippedNoLeadId} skipped (no lead ID)`);
      toast.success(parts.join(", ") || "Enrichment complete");

      setSelectedLeadIds(new Set());
      if (result.leads?.length) {
        const updatedMap = new Map(result.leads.map((l) => [l.id, l]));
        setLeads((prev) =>
          prev.map((l) => updatedMap.get(l.id) ?? l)
        );
      }
      if (result.validationJob) {
        setValidationProgress({
          jobId: result.validationJob.jobId,
          phase: "started",
          processed: 0,
          total: result.validationJob.totalEmails,
          percentComplete: 0,
        });
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to enrich leads");
    } finally {
      setEnriching(false);
    }
  };

  const handleSendEmailSelected = () => {
    if (selectedLeadIds.size === 0) return;
    const selected = leads.filter((l) => selectedLeadIds.has(l.id));

    // Block send if any lead is still being validated
    const validationInProgress = selected.some(
      (l) =>
        l.emailValidationStatus === "pending" ||
        l.emailValidationStatus === "in_progress"
    );
    if (validationInProgress) {
      toast.error(
        "Email validation is still in progress. Please wait before sending."
      );
      return;
    }

    const withoutEmails = selected.filter((l) => {
      const emailList = Array.isArray(l.emails) ? l.emails : [];
      return !l.enrichedAt || emailList.length === 0;
    });

    if (withoutEmails.length > 0) {
      setEmailErrorModal({ open: true, leadsWithoutEmails: withoutEmails });
      return;
    }

    setEmailConfirmModal({ open: true, eligibleLeads: selected });
  };

  const handleConfirmSendEmail = async () => {
    setSendingEmail(true);
    try {
      const result = await adminLeadsService.sendEmail(
        Array.from(selectedLeadIds)
      );
      const parts: string[] = [];
      if (result.sent > 0) parts.push(`${result.sent} sent`);
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
      if (result.failed > 0) parts.push(`${result.failed} failed`);
      if (result.skippedSuppressed > 0) {
        parts.push(`${result.skippedSuppressed} skipped (suppressed)`);
      }
      if (result.skippedUndeliverable > 0) {
        parts.push(`${result.skippedUndeliverable} skipped (undeliverable)`);
      }
      if (result.skippedCap > 0) {
        parts.push(`${result.skippedCap} skipped (daily cap)`);
      }
      if (result.skippedDuplicate > 0) {
        parts.push(`${result.skippedDuplicate} duplicate merges`);
      }
      toast.success(parts.join(", ") || "Emails sent");

      setSelectedLeadIds(new Set());
      setEmailConfirmModal({ open: false, eligibleLeads: [] });
      fetchLeads();
    } catch (error: any) {
      toast.error(error.message || "Failed to send emails");
    } finally {
      setSendingEmail(false);
    }
  };

  const formatPurgeDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const csvCount =
    stats?.leadsBySource.find((s) => s.source === "csv")?.count ?? 0;
  const scCount =
    stats?.leadsBySource.find((s) => s.source === "store_census")?.count ?? 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Lead Generation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate and manage leads from StoreCensus and CSV imports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openPurgeModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-card border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-300 dark:hover:border-red-500/40 transition-colors"
          >
            <Trash2 size={16} />
            Purge Leads
          </button>
          <button
            onClick={() => {
              fetchStats();
              fetchLeads();
              fetchSavedFilters();
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-lg hover:bg-accent"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Leads"
              value={stats?.totalLeads ?? 0}
              icon={<Users size={24} />}
              warm
            />
            <StatCard
              title="StoreCensus"
              value={scCount}
              icon={<Globe size={24} />}
            />
            <StatCard
              title="CSV Import"
              value={csvCount}
              icon={<FileSpreadsheet size={24} />}
            />
            <StatCard
              title="Countries"
              value={stats?.leadsByCountry.length ?? 0}
              icon={<Database size={24} />}
            />
          </>
        )}
      </div>

      {/* Tab Bar */}
      <UnderlineTabs<TabKey>
        accent="terracotta"
        value={activeTab}
        onValueChange={setActiveTab}
        tabs={[
          {
            value: "all",
            label: (
              <span className="flex items-center gap-2">
                <Database size={16} />
                All Leads
              </span>
            ),
          },
          {
            value: "generate",
            label: (
              <span className="flex items-center gap-2">
                <Zap size={16} />
                Generate from StoreCensus
              </span>
            ),
          },
        ]}
      />

      {/* Tab Content */}
      {activeTab === "all" && (
        <div className="space-y-4">
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-muted-foreground" />
              </div>
              <input
                type="text"
                placeholder="Search by domain or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b] text-sm bg-card"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2.5 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]"
            >
              <option value="">All Sources</option>
              <option value="csv">CSV</option>
              <option value="store_census">StoreCensus</option>
            </select>
            <select
              value={contactTypeFilter}
              onChange={(e) => {
                setContactTypeFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2.5 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]"
            >
              <option value="">All Contacts</option>
              <option value="email">Has Email</option>
              <option value="phone">Has Phone</option>
              <option value="both">Has Both</option>
              <option value="any">Has Any Contact</option>
            </select>
            <select
              value={savedFilterIdFilter}
              onChange={(e) => {
                setSavedFilterIdFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2.5 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]"
            >
              <option value="">All Filters</option>
              {savedFilters.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            <MultiSelectDropdown
              options={VERTICAL_OPTIONS}
              selected={verticalFilter}
              onChange={(v) => {
                setVerticalFilter(v);
                setPage(1);
              }}
              searchable
              placeholder="All niches..."
              showSelectedTags={false}
              className="w-56"
            />
          </div>

          <LeadsTable
            leads={leads}
            loading={leadsLoading}
            selectedIds={selectedLeadIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            allSelected={leads.length > 0 && selectedLeadIds.size === leads.length}
            footer={
              totalLeads > 0 ? (
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  total={totalLeads}
                  pageSize={limit}
                  onPageChange={setPage}
                  pageSizeOptions={[25, 50, 75, 100]}
                  onPageSizeChange={(size) => {
                    setLimit(size);
                    setPage(1);
                  }}
                  noun="leads"
                />
              ) : undefined
            }
          />
        </div>
      )}

      {activeTab === "generate" && (
        <div className="flex gap-6">
          {/* Saved Filters Sidebar */}
          <div className="w-64 flex-shrink-0">
            <SavedFiltersSidebar
              filters={savedFilters}
              loading={savedFiltersLoading}
              onLoad={handleLoadFilter}
              onRun={handleRunSavedFilter}
              onDelete={handleDeleteFilter}
              onSetDefault={handleSetDefault}
            />
          </div>

          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Filter builder */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">
                  StoreCensus Filters
                </h2>
                <button
                  onClick={() => setScFilters({})}
                  className="text-sm text-muted-foreground hover:text-muted-foreground"
                >
                  Clear All
                </button>
              </div>

              <StoreCensusFilterBuilder
                filters={scFilters}
                onFiltersChange={setScFilters}
                onSaveFilter={handleSaveFilter}
              />

              <div className="mt-6 space-y-2">
                <label htmlFor="generate-batch-size" className="block text-xs font-medium text-muted-foreground">
                  Batch size
                </label>
                <p className="text-xs text-muted-foreground">
                  Rows fetched from Store Census each run (maximum 1,000 per request).
                </p>
                <select
                  id="generate-batch-size"
                  value={generatePageSize}
                  onChange={(e) => setGeneratePageSize(Number(e.target.value))}
                  disabled={generateLoading}
                  className="w-full max-w-xs px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b] disabled:opacity-50"
                >
                  {GENERATE_BATCH_SIZES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <button
                  onClick={handleGenerate}
                  disabled={generateLoading}
                  className="w-full px-6 py-3 text-sm font-semibold text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: "#d7785b" }}
                >
                  {generateLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap size={16} />
                      Generate Leads
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Results */}
            {generateResult && (
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">
                    Results
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                    <span>
                      Total matching{" "}
                      <span className="text-muted-foreground">(Store Census)</span>:{" "}
                      <strong className="text-foreground">
                        {generateResult.total.toLocaleString()}
                      </strong>
                    </span>
                    <span>
                      Imported{" "}
                      <span className="text-muted-foreground">(this run)</span>:{" "}
                      <strong className="text-foreground">
                        {generateResult.imported}
                      </strong>
                    </span>
                    <span>
                      Skipped{" "}
                      <span className="text-muted-foreground">(this run)</span>:{" "}
                      <strong className="text-foreground">
                        {generateResult.skipped}
                      </strong>
                    </span>
                    <span>
                      Fetched{" "}
                      <span className="text-muted-foreground">(this batch)</span>:{" "}
                      <strong className="text-foreground">
                        {generateResult.fetched}
                      </strong>
                    </span>
                  </div>
                </div>

                <LeadsTable
                  leads={generatedLeads}
                  loading={false}
                  onLoadMore={
                    generateResult.hasMore ? handleLoadMore : undefined
                  }
                  hasMore={generateResult.hasMore}
                  loadingMore={loadingMore}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Validation Progress Banner */}
      {validationProgress && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 min-w-[360px]">
          <div className="flex flex-col gap-2 px-5 py-3 bg-card border border-border rounded-xl shadow-xl text-foreground">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Loader2 size={14} className="animate-spin text-[#d7785b]" />
                <span>
                  {validationProgress.phase === "started"
                    ? "Email validation queued…"
                    : "Email validation in progress…"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {validationProgress.percentComplete > 0 ? (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {Math.round(validationProgress.percentComplete)}%
                  </span>
                ) : validationProgress.total > 0 ? (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {validationProgress.processed}/{validationProgress.total}
                  </span>
                ) : null}
                <button
                  onClick={() => setValidationProgress(null)}
                  className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-muted-foreground transition-colors"
                  title="Dismiss (validation continues in background)"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
            {/* Progress bar: prefer percentComplete, fall back to processed/total */}
            {(validationProgress.percentComplete > 0 || validationProgress.processed > 0 || validationProgress.total > 0) && (
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#d7785b] rounded-full transition-all duration-500"
                  style={{
                    width: validationProgress.percentComplete > 0
                      ? `${Math.min(validationProgress.percentComplete, 100)}%`
                      : validationProgress.total > 0
                      ? `${Math.round((validationProgress.processed / validationProgress.total) * 100)}%`
                      : "0%",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selection Action Bar */}
      {selectedLeadIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-3 px-5 py-3 bg-foreground text-background rounded-xl shadow-2xl">
            <span className="text-sm font-medium">
              {selectedLeadIds.size} lead{selectedLeadIds.size !== 1 ? "s" : ""} selected
            </span>
            <div className="w-px h-5 bg-background/30" />
            <button
              onClick={handleEnrichSelected}
              disabled={enriching || sendingEmail || !!validationProgress}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              style={{ backgroundColor: "#d7785b" }}
            >
              {enriching ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Enriching...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Enrich Contacts
                </>
              )}
            </button>
            <button
              onClick={handleSendEmailSelected}
              disabled={sendingEmail || enriching || !!validationProgress}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 bg-blue-600 hover:bg-blue-700"
            >
              <Mail size={14} />
              Send Email
            </button>
            <button
              onClick={() => setSelectedLeadIds(new Set())}
              className="p-1.5 rounded-lg hover:bg-background/20 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Generate Loading Modal — stays open until generation finishes */}
      <DialogRoot
        open={generateLoading}
        onOpenChange={() => {}}
        panelClassName="max-w-sm"
      >
        <DialogContent className="p-10 flex flex-col items-center gap-5">
          <div className="relative h-16 w-16">
            <div className="absolute inset-0 rounded-full border-4 border-border" />
            <div className="absolute inset-0 rounded-full border-4 border-t-[#d7785b] animate-spin" />
          </div>
          <div className="text-center space-y-1.5">
            <h3 className="text-lg font-semibold text-foreground">
              Generating Leads
            </h3>
            <p className="text-sm text-muted-foreground">
              Fetching and importing leads from StoreCensus. This may take a moment...
            </p>
          </div>
        </DialogContent>
      </DialogRoot>

      {/* Purge Modal */}
      <DialogRoot
        open={purgeOpen}
        onOpenChange={(open) => {
          if (!open && !purging) setPurgeOpen(false);
        }}
        panelClassName="max-w-lg max-h-[80vh] flex flex-col"
      >
        <DialogHeader
          className="p-6 border-b border-border mb-0"
          showCloseButton
          onClose={() => { if (!purging) setPurgeOpen(false); }}
        >
          <span className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
              <Trash2 size={20} className="text-red-500" />
            </span>
            <span>
              <span className="block text-lg font-semibold text-foreground">Purge Leads</span>
              <span className="block text-xs font-normal text-muted-foreground">Select a date to remove all leads created on that day</span>
            </span>
          </span>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
              {purgeDatesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
              ) : purgeDates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  No leads found.
                </div>
              ) : (
                <div className="space-y-2">
                  {purgeDates.map((entry) => (
                    <div
                      key={entry.date}
                      className="flex items-center justify-between p-3 rounded-xl border border-border hover:border-border transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {formatPurgeDate(entry.date)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {entry.count.toLocaleString()} {entry.count === 1 ? "lead" : "leads"}
                        </p>
                      </div>

                      {confirmDate?.date === entry.date ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setConfirmDate(null)}
                            disabled={!!purging}
                            className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handlePurge(entry.date)}
                            disabled={!!purging}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                          >
                            {purging === entry.date ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <AlertTriangle size={12} />
                            )}
                            Confirm
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDate(entry)}
                          disabled={!!purging}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-300 dark:hover:border-red-500/40 transition-colors disabled:opacity-50"
                        >
                          Purge
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
        </div>
      </DialogRoot>

      {/* Email Error Modal - leads without email contacts */}
      <DialogRoot
        open={emailErrorModal.open}
        onOpenChange={(open) => {
          if (!open) setEmailErrorModal({ open: false, leadsWithoutEmails: [] });
        }}
        panelClassName="max-w-md max-h-[80vh] flex flex-col"
      >
        <DialogHeader
          className="p-6 border-b border-border mb-0"
          showCloseButton
          onClose={() =>
            setEmailErrorModal({ open: false, leadsWithoutEmails: [] })
          }
        >
          <span className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
              <AlertCircle size={20} className="text-red-500" />
            </span>
            <span>
              <span className="block text-lg font-semibold text-foreground">
                Missing Email Contacts
              </span>
              <span className="block text-xs font-normal text-muted-foreground">
                Some selected leads don't have email contacts
              </span>
            </span>
          </span>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm text-muted-foreground mb-4">
                The following{" "}
                {emailErrorModal.leadsWithoutEmails.length === 1
                  ? "lead has"
                  : `${emailErrorModal.leadsWithoutEmails.length} leads have`}{" "}
                not been enriched or have no email contacts. Please enrich them
                first before sending emails.
              </p>
              <div className="space-y-2">
                {emailErrorModal.leadsWithoutEmails.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center gap-2 p-3 rounded-lg border border-border"
                  >
                    <Globe size={14} className="text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {lead.domain}
                      </p>
                      {lead.companyName && (
                        <p className="text-xs text-muted-foreground truncate">
                          {lead.companyName}
                        </p>
                      )}
                    </div>
                    <span className="ml-auto text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                      {!lead.enrichedAt ? "Not enriched" : "No emails"}
                    </span>
                  </div>
                ))}
              </div>
        </div>

        <DialogFooter className="p-6 border-t border-border mt-0">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() =>
              setEmailErrorModal({ open: false, leadsWithoutEmails: [] })
            }
          >
            Close
          </Button>
        </DialogFooter>
      </DialogRoot>

      {/* Email Confirmation Modal */}
      <DialogRoot
        open={emailConfirmModal.open}
        onOpenChange={(open) => {
          if (!open && !sendingEmail)
            setEmailConfirmModal({ open: false, eligibleLeads: [] });
        }}
        panelClassName="max-w-md max-h-[80vh] flex flex-col"
      >
        <DialogHeader
          className="p-6 border-b border-border mb-0"
          showCloseButton
          onClose={() => {
            if (!sendingEmail)
              setEmailConfirmModal({ open: false, eligibleLeads: [] });
          }}
        >
          <span className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
              <Send size={20} className="text-blue-600 dark:text-blue-400" />
            </span>
            <span>
              <span className="block text-lg font-semibold text-foreground">
                Send Promotional Email
              </span>
              <span className="block text-xs font-normal text-muted-foreground">
                Confirm sending to {emailConfirmModal.eligibleLeads.length}{" "}
                lead
                {emailConfirmModal.eligibleLeads.length !== 1 ? "s" : ""}
              </span>
            </span>
          </span>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm text-muted-foreground mb-4">
                This sends at most{" "}
                <span className="font-medium">
                  one best outreach email per store
                </span>{" "}
                (validated / deliverability ranked). Stores with only
                undeliverable contacts are skipped automatically.
              </p>
              <div className="space-y-2">
                {emailConfirmModal.eligibleLeads.map((lead) => {
                  const emailCount = Array.isArray(lead.emails)
                    ? lead.emails.length
                    : 0;
                  const outboundLabel =
                    emailCount > 0 ? "≤1 outreach" : "No targets";
                  return (
                    <div
                      key={lead.id}
                      className="flex items-center gap-2 p-3 rounded-lg border border-border"
                    >
                      <Globe
                        size={14}
                        className="text-muted-foreground flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {lead.domain}
                        </p>
                        {lead.companyName && (
                          <p className="text-xs text-muted-foreground truncate">
                            {lead.companyName}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {outboundLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

        <DialogFooter className="p-6 border-t border-border mt-0">
          <button
            onClick={() =>
              setEmailConfirmModal({ open: false, eligibleLeads: [] })
            }
            disabled={sendingEmail}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-muted-foreground bg-muted rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmSendEmail}
            disabled={sendingEmail}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            {sendingEmail ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={14} />
                Send Emails
              </>
            )}
          </button>
        </DialogFooter>
      </DialogRoot>
    </div>
  );
}
